import { getClientEnvironmentDetails } from '@sm/utils';
import { isGDPRCountry } from '@sm/locale';
import {
  DigitalData,
  DigitalDataEvent,
  EnvironmentDetails,
  MetricsTrackerType,
  MetricsTrackerConfigurationType
} from './types/types';
import USER_EVENTS from './types/enums';
import getCookieValue from './utils';
import { pageViewMutation } from './helpers';

import dataAnalyticsMetricsSubscriber from './subscribers/DataAnalyticsMetricsSubscriber';
import loggingMetricsSubscriber from './subscribers/LoggingMetricsSubscriber';
import gtmMetricsSubscriber from './subscribers/GTMMeticsSubscriber';
import facebookPixelSubscriber from './subscribers/FacebookPixelSubscriber';

const { isBrowser } = getClientEnvironmentDetails<EnvironmentDetails>();

// Import/require only in the browser
const delegatedEvents = isBrowser ? require('delegated-events') : null;

function hasGDPRConsent(country: string): boolean {
  return !isGDPRCountry(country) || getCookieValue('gdpr_consent') === 'true';
}

function getCleanDigitalData(userId: string = '1'): DigitalData {
  return {
    page: {
      pageInfo: {
        pageId: '',
        attributes: {},
        pathname: ''
      }
    },
    events: [],
    components: [],
    user: {
      ep201: getCookieValue('ep201'),
      ep202: getCookieValue('ep202'),
      id: userId
    }
  };
}

export function generateMetricsAttribute(eventData: {
  name?: USER_EVENTS;
  data: Record<string, any>;
}): String {
  return JSON.stringify({
    name: eventData.name || USER_EVENTS.ELEMENT_INTERACTION,
    data: eventData.data
  });
}

export const MetricsTracker: MetricsTrackerType = {
  subscribers: [],

  config: {
    userId: '',
    dataAnalyticsAPIPath: null,
    loggingAPIPath: null,
    country: 'GB',
    legacyWeb: '',
    gtmId: ''
  },

  digitalData: getCleanDigitalData(),

  initialize(config: MetricsTrackerConfigurationType) {
    this.config = config;
    this.digitalData = getCleanDigitalData(config.userId);
  },

  reset() {
    this.digitalData = getCleanDigitalData(this.config.userId);
    this.subscribers = [];
    // Add the mandatory dataAnalytics subscriber
    this.addSubscriber(dataAnalyticsMetricsSubscriber);
    // Add the mandatory logging subscriber
    this.addSubscriber(loggingMetricsSubscriber);
    // Add Google Tag Manager dataLayer subscriber
    MetricsTracker.addSubscriber(gtmMetricsSubscriber);
    // Add Facebook Pixel subscriber
    MetricsTracker.addSubscriber(facebookPixelSubscriber);
  },

  getSubscribers() {
    return this.subscribers;
  },

  track(event: DigitalDataEvent) {
    if (this.config.userId === '') {
      throw new Error(
        'Please initialize MetricsTracker with a userId before tracking events.'
      );
    }

    if (USER_EVENTS[event.name]) {
      this.digitalData.events.push(event);

      if (USER_EVENTS.COMPONENT_ADD === event.name) {
        this.digitalData.components.push(event.data);
      }

      if (event.name === USER_EVENTS.PAGE_VIEW) {
        this.digitalData = pageViewMutation(event, this.digitalData);
      }

      if (!hasGDPRConsent(this.config.country)) {
        gtmMetricsSubscriber(event);
      } else {
        this.subscribers.forEach(subscriber => {
          subscriber(event, this.config);
        });
      }
    }
  },

  addSubscriber(
    subscriber: (
      digitalDataEvent: DigitalDataEvent,
      metricsTrackerConfig: MetricsTrackerConfigurationType
    ) => void
  ): void {
    /**
     * We always want to allow the subscribers to subscribe. If the user
     * did not accept GDPR, we don't fire events; hence, we never violate GDPR.
     * If, however, the user does allow tracking, we don't want to subscribers
     * to re-subscribe.
     */
    this.subscribers.push(subscriber);
  },

  getDigitalData(): DigitalData {
    return this.digitalData;
  }
};

if (isBrowser) {
  /**
   * Add on click event listener
   * Note - this is done in the getInstance method; hence, until this
   * method is called, the event handler would never be bound.
   */
  delegatedEvents.on('click', '[data-sm-metrics]', function gce(
    event: MouseEvent
  ) {
    if (event && event.currentTarget) {
      const metricsData = (event.currentTarget as HTMLElement).getAttribute(
        'data-sm-metrics'
      );
      if (metricsData) {
        const metricsDataJSON = JSON.parse(metricsData);
        const eventData = metricsDataJSON.data;

        MetricsTracker.track({
          name: metricsDataJSON.name || USER_EVENTS.ELEMENT_INTERACTION,
          data: eventData
        });
      }
    }
  });
  // Add the mandatory dataAnalytics subscriber
  MetricsTracker.addSubscriber(dataAnalyticsMetricsSubscriber);
  // Add the mandatory logging subscriber
  MetricsTracker.addSubscriber(loggingMetricsSubscriber);
  // Add Google Tag Manager dataLayer subscriber
  MetricsTracker.addSubscriber(gtmMetricsSubscriber);
  // Add Facebook Pixel subscriber
  MetricsTracker.addSubscriber(facebookPixelSubscriber);

  if (!window.SM || !window.SM.MetricsTracker) {
    window.SM = window.SM || {};
    window.SM.MetricsTracker = MetricsTracker;
  }
}
